import { queryKeys, useHttpClient } from '@repo/api-client/react';
import {
  RenderInvoiceResponseSchema,
  type RenderInvoiceRequest,
  type RenderInvoiceResponse,
} from '@repo/api-client/dtos';
import type { AxiosInstance } from 'axios';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';

import { getJSON, postJSON } from '../internal/http';

/**
 * Fetch (and refetch) the latest cached preview for an invoice.
 *
 * The endpoint either responds with `{ status: 'ready', previewUrl, pdfUrl }`
 * (cache hit) or kicks off a background render and returns
 * `{ status: 'queued', jobId }`. The hook returns the parsed response
 * verbatim — the consumer decides whether to display the URL or
 * spin until a worker finishes.
 */
export function useInvoicePreview(
  orgId: string,
  invoiceId: string,
  options?: { enabled?: boolean },
) {
  const http = useHttpClient();
  const enabled = Boolean(orgId && invoiceId) && (options?.enabled ?? true);

  const query = useQuery({
    queryKey: queryKeys.printer.preview(orgId, invoiceId),
    queryFn: () =>
      getJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/preview`,
        RenderInvoiceResponseSchema,
      ),
    enabled,
    // Cached previews stay valid until the invoice mutates; let the
    // mutation hooks own invalidation rather than poll here.
    staleTime: 5 * 60_000,
  });

  return {
    preview: query.data ?? null,
    /** True only on the initial load (no cached data yet). */
    loading: query.isLoading,
    /** True during background refetches — do not swap UI to a spinner. */
    fetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Polling status hook for an in-flight render job. Stops polling
 * once the server reports `ready` or `failed`. Use it in tandem
 * with `useRenderInvoice` — kick off the job, then watch the
 * status until the URLs are ready, then call `Sharing.shareAsync`.
 */
export function useRenderJobStatus(
  orgId: string,
  invoiceId: string,
  jobId: string | null,
) {
  const http = useHttpClient();
  const query = useQuery({
    queryKey: jobId
      ? queryKeys.printer.job(orgId, invoiceId, jobId)
      : ['printer', 'job', 'idle'],
    queryFn: () =>
      getJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/render/${jobId}`,
        RenderInvoiceResponseSchema,
      ),
    enabled: Boolean(jobId),
    refetchInterval: (q) => {
      const data = q.state.data as RenderInvoiceResponse | undefined;
      if (!data) return 1500;
      return data.status === 'ready' || data.status === 'failed' ? false : 1500;
    },
  });
  return {
    job: query.data ?? null,
    loading: query.isLoading,
    error: query.error,
  };
}

/**
 * Kick off (or fetch a cached) render. Returns the parsed response
 * so callers can branch on `status === 'ready'` vs `'queued'`.
 *
 * Two call surfaces:
 *
 *   - `render(vars)` — fire-and-forget, resolves with whatever the
 *     server returns (`ready` | `queued` | `working` | `failed`).
 *     Use when you want optimistic UI and don't care about the PDF
 *     URL (e.g. pre-warming a preview after an edit).
 *
 *   - `renderAndWait(vars, opts)` — kicks off the render and then
 *     polls the job endpoint until the artifact lands. Resolves with
 *     a `'ready'` response (with `pdfUrl` + `previewUrl`) or rejects
 *     with `RenderError`. Use when you need the artifact in hand
 *     (Share PDF, Download).
 *
 * Successful runs invalidate the preview cache so any thumbnail
 * picks up the fresh URL on the next render.
 */
export function useRenderInvoice(orgId: string, invoiceId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (vars: RenderInvoiceRequest = {}) =>
      postJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/render`,
        vars,
        RenderInvoiceResponseSchema,
      ),
    onSuccess: (response) => {
      if (response.status === 'ready') {
        queryClient.setQueryData(
          queryKeys.printer.preview(orgId, invoiceId),
          response,
        );
      }
    },
  });

  const renderAndWait = useCallback(
    async (
      vars: RenderInvoiceRequest = {},
      opts: AwaitRenderOpts = {},
    ): Promise<ReadyRenderResponse> => {
      // Wrap the kick-off so callers always see a `RenderError`,
      // never a raw AxiosError. Lets the UI surface a single
      // friendly message regardless of which step blew up.
      let initial: RenderInvoiceResponse;
      try {
        initial = await mutation.mutateAsync(vars);
      } catch (err) {
        if (err instanceof RenderError) throw err;
        throw new RenderError(
          `Render request failed: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      const initialReady = asReady(initial);
      if (initialReady) return initialReady;
      if (initial.status === 'failed') {
        throw new RenderError(initial.error ?? 'Render failed', initial);
      }
      if (!initial.jobId) {
        throw new RenderError('Render queued without a job id', initial);
      }
      const ready = await pollRenderJob(
        http,
        orgId,
        invoiceId,
        initial.jobId,
        opts,
      );
      queryClient.setQueryData(
        queryKeys.printer.preview(orgId, invoiceId),
        ready,
      );
      return ready;
    },
    [http, mutation, orgId, invoiceId, queryClient],
  );

  return {
    render: mutation.mutateAsync,
    renderAndWait,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

// ---------------------------------------------------------------------------

export interface AwaitRenderOpts {
  /** Total time to wait before giving up (default 60s). */
  timeoutMs?: number;
  /** Interval between job polls (default 1500ms). */
  pollIntervalMs?: number;
  /**
   * Optional cancellation signal. The poll loop checks `signal.aborted`
   * between polls and rejects with a `RenderError` describing the
   * abort. Use to bail out of a `renderAndWait` when the user closes
   * the screen mid-wait.
   */
  signal?: AbortSignal;
}

/**
 * `RenderInvoiceResponse` narrowed to the success case — `status`
 * pinned to `'ready'` and the URL fields promoted to required so
 * downstream code (downloaders, share sheets) doesn't have to
 * re-check.
 */
export type ReadyRenderResponse = RenderInvoiceResponse & {
  status: 'ready';
  pdfUrl: string;
  previewUrl: string;
};

function asReady(
  response: RenderInvoiceResponse | null | undefined,
): ReadyRenderResponse | null {
  if (!response) return null;
  if (response.status !== 'ready') return null;
  if (!response.pdfUrl || !response.previewUrl) return null;
  return response as ReadyRenderResponse;
}

/**
 * Thrown when `renderAndWait` cannot return a `'ready'` response —
 * server reported failure, the wait exceeded `timeoutMs`, or the
 * caller aborted via `opts.signal`.
 */
export class RenderError extends Error {
  readonly response: RenderInvoiceResponse | null;
  constructor(message: string, response: RenderInvoiceResponse | null = null) {
    super(message);
    this.name = 'RenderError';
    this.response = response;
  }
}

/**
 * Poll the render-job endpoint until it reports `'ready'` or
 * `'failed'`, or until the timeout elapses. Used by `renderAndWait`
 * but also exported as a building block — long-form flows like the
 * artifact downloader compose it directly.
 */
async function pollRenderJob(
  http: AxiosInstance,
  orgId: string,
  invoiceId: string,
  jobId: string,
  opts: AwaitRenderOpts,
): Promise<ReadyRenderResponse> {
  const timeoutMs = opts.timeoutMs ?? 60_000;
  const pollIntervalMs = opts.pollIntervalMs ?? 1500;
  const start = Date.now();

  // Loop until either the artifact is ready, the server reports a
  // terminal failure, the budget is exhausted, or the caller bails.
  // Wrap getJSON in a try/catch so a transient network blip during
  // polling becomes a `RenderError` (always our error class) rather
  // than leaking AxiosError or a parse-time TypeError to callers.
  while (true) {
    if (opts.signal?.aborted) {
      throw new RenderError('Render aborted by caller');
    }
    const elapsed = Date.now() - start;
    if (elapsed >= timeoutMs) {
      throw new RenderError(
        `Render did not complete in ${timeoutMs}ms`,
      );
    }
    const remaining = timeoutMs - elapsed;
    const delay = Math.min(pollIntervalMs, remaining);
    await sleep(delay, opts.signal);

    let status: RenderInvoiceResponse;
    try {
      status = await getJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/render/${jobId}`,
        RenderInvoiceResponseSchema,
      );
    } catch (err) {
      if (err instanceof RenderError) throw err;
      throw new RenderError(
        `Render status check failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
    const ready = asReady(status);
    if (ready) return ready;
    if (status.status === 'failed') {
      throw new RenderError(status.error ?? 'Render failed', status);
    }
    // 'queued' / 'working' — keep polling.
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new RenderError('Render aborted by caller'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new RenderError('Render aborted by caller'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
